import { ReactElement } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useFormikContext } from 'formik'
import { Button, Divider, Grid, H, P } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import { ConfirmModal } from 'components/confirm-modal'
import MessageBox from 'components/message-box'
import { formatCurrency, hasDecimals } from 'lib/formatting/currency'
import {
  FuneralPlan,
  FuneralPlanAttributes,
  FuneralPlanPaymentDetails,
} from 'lib/models/funeral-plan'
import { removeFuneralPlanCelebration, showModal } from 'state/actions'
import { removeTextAndFormat } from 'utils/helpers'
import { getCelebrationItems } from './celebration-helpers'

const CelebrationList = styled.ul`
  margin: ${GTR.XS} 0 0;
  padding-left: ${GTR.M};
`

/** Summary of the celebration on the plan. With `canRemove` it can also be
 * removed: the celebration and urn come off the plan, and the lump-sum refund
 * is done manually on Stripe.
 * Note: A celebration added at pre-purchase and included in the instalments cannot
 * be removed for now.
 * @TODO add ability to remove celebration paid in instalments when we have it. */
const CelebrationDetails = ({
  canRemove = false,
}: {
  canRemove?: boolean
}): ReactElement | null => {
  const dispatch = useDispatch()
  const { values } = useFormikContext<FuneralPlanAttributes>()
  const funeralPlan = useSelector(
    (state: { funeralPlanCase: FuneralPlan }) => state.funeralPlanCase
  )

  const celebrationPriceInPence = useSelector(
    (state: { funeralPlanPaymentDetails: FuneralPlanPaymentDetails }) =>
      state.funeralPlanPaymentDetails.attributes?.celebrationPriceInPence
  )
  const celebrationItems = getCelebrationItems(values.items)

  if (celebrationItems.length === 0) return null

  return (
    <>
      <Grid.Item data-testid="celebration-details">
        <H size="S">Celebration</H>
        <P margin={0}>Chosen celebration:</P>
        <CelebrationList>
          {celebrationItems.map((item) => (
            <li key={item.slug}>
              {removeTextAndFormat({
                name: item.slug,
                textToRemove: 'celebration_',
              })}
            </li>
          ))}
        </CelebrationList>
        {canRemove && funeralPlan.attributes.status === 'active' && (
          <>
            {funeralPlan.attributes.paymentType === 'instalments' &&
            !funeralPlan.attributes.celebrationPaidSeparatelyInFull ? (
              <MessageBox margin={['S', 0, 0]}>
                This celebration was bought with the plan and is part of their
                monthly instalments, so it can’t be removed for now.
              </MessageBox>
            ) : (
              <Button.Underline
                type="button"
                margin={['S', 0, 0]}
                onClick={() =>
                  dispatch(
                    showModal({
                      headingText: 'Remove this celebration?',
                      component: () => (
                        <ConfirmModal
                          onConfirm={async () => {
                            await dispatch(
                              removeFuneralPlanCelebration(funeralPlan.id)
                            )
                          }}
                          confirmButtonText="Remove the celebration"
                          confirmButtonSubmittingText="Removing..."
                        >
                          <P>
                            The celebration and its complimentary urn will be
                            removed from the plan.
                          </P>
                          <P>
                            Make sure to refund the customer their
                            {celebrationPriceInPence
                              ? ` ${formatCurrency({
                                  showPence: hasDecimals(
                                    celebrationPriceInPence / 100
                                  ),
                                  value: celebrationPriceInPence,
                                  valueInPence: true,
                                })}`
                              : ''}{' '}
                            payment on Stripe.
                          </P>
                        </ConfirmModal>
                      ),
                    })
                  )
                }
              >
                Remove celebration
              </Button.Underline>
            )}
          </>
        )}
      </Grid.Item>
      <Divider margin={['L', 0]} />
    </>
  )
}

export default CelebrationDetails
